import { motion } from "framer-motion";
import { MessageCircle, Mail, Instagram, Facebook } from "lucide-react";
import { Page } from "@/components/Page";
import { useSettings, buildWaLink } from "@/context/SettingsContext";

const igUrl = (h) => (h ? (h.startsWith("http") ? h : `https://instagram.com/${h.replace(/^@/, "")}`) : "#");
const fbUrl = (h) => (h ? (h.startsWith("http") ? h : `https://facebook.com/${h}`) : "#");

export default function Contact() {
  const { settings } = useSettings();
  const c = settings?.contacts || {};
  const cards = [
    { icon: MessageCircle, label: "WhatsApp", value: c.whatsapp || "", href: buildWaLink(c), color: "bg-[#25D366]", testid: "contact-whatsapp" },
    { icon: Mail, label: "Email", value: c.email || "", href: `mailto:${c.email || ""}`, color: "brand-gradient", testid: "contact-email" },
    { icon: Instagram, label: "Instagram", value: c.instagram ? `@${c.instagram.replace(/^@/, "")}` : "", href: igUrl(c.instagram), color: "bg-gradient-to-r from-[#F58529] to-[#DD2A7B]", testid: "contact-instagram" },
    { icon: Facebook, label: "Facebook", value: c.facebook || "", href: fbUrl(c.facebook), color: "bg-[#1877F2]", testid: "contact-facebook" },
  ];

  return (
    <Page className="pt-32 md:pt-40 px-6 md:px-12 max-w-5xl mx-auto min-h-screen">
      <div className="text-center max-w-2xl mx-auto">
        <span className="inline-block rounded-full bg-accent text-accent-foreground text-sm font-semibold px-4 py-2">Contactos</span>
        <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter mt-4">Fala <span className="text-gradient">connosco</span></h1>
        <p className="text-muted-foreground mt-4 text-lg">Tens uma dúvida, uma encomenda ou uma ideia personalizada? Escolhe o teu canal preferido — respondemos rapidinho!</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mt-12">
        {cards.map((card, i) => (
          <motion.a
            key={card.label}
            href={card.href} target="_blank" rel="noreferrer"
            data-testid={card.testid}
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
            className="group flex items-center gap-5 rounded-3xl border border-border bg-card p-6 hover:shadow-[0_20px_50px_rgb(0,0,0,0.08)] transition-shadow"
          >
            <span className={`grid place-items-center w-14 h-14 rounded-2xl text-white ${card.color} transition-transform group-hover:scale-110`}>
              <card.icon size={24} />
            </span>
            <div>
              <p className="text-sm text-muted-foreground font-medium">{card.label}</p>
              <p className="font-display font-bold text-lg break-all">{card.value}</p>
            </div>
          </motion.a>
        ))}
      </div>

      <div className="mt-12 rounded-[2.5rem] brand-gradient text-white p-10 md:p-14 text-center relative overflow-hidden grain">
        <h2 className="relative font-display font-black text-3xl md:text-4xl">Prefere falar já?</h2>
        <p className="relative text-white/85 mt-3">Clica no botão e inicia uma conversa no WhatsApp agora mesmo.</p>
        <a href={buildWaLink(c)} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-cta"
          className="relative inline-flex items-center gap-2 mt-6 rounded-full bg-white text-foreground font-bold px-8 py-4 transition-transform hover:scale-105 active:scale-95">
          <MessageCircle size={20} /> Abrir WhatsApp
        </a>
      </div>
    </Page>
  );
}
